import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Eye, RotateCcw, Save, Send } from 'lucide-react';
import api from '../services/api';
import PageHeader from '../components/PageHeader';
import { Btn, ConfirmModal, Empty, ErrorBox, Skeleton } from '../ui/kit';
import { datumUndZeit } from '../lib/zeit';
import './MailTemplates.css';

// ─────────────────────────────────────────────────────────────────────────────
// MAIL-VORLAGEN-001 — E-Mail-Texte ändern, ohne Deploy.
//
// Jede Mail an Kunden, Fahrer und Partner hat hier eine Vorlage. Ohne Änderung gilt der
// Standardtext aus dem Backend-Katalog; eine gespeicherte Änderung gilt ab der NÄCHSTEN Mail.
// Die Seite prüft nichts selbst: gültig ist, was der Server annimmt (Vorschau und Speichern
// nennen jeden Fehler im Klartext). Geschrieben wird nur Fließtext — nie HTML.
// Backend: /admin/mail-templates (GET Liste, GET/PUT :key, POST :key/preview|reset|test).
// ─────────────────────────────────────────────────────────────────────────────

const fehlertext = (e, fallback) => e?.response?.data?.message || e?.response?.data?.error || fallback;
// Zeitanzeige ausschließlich über den Admin-Zeithelfer (Server liefert SQLite-UTC ohne Kennung).
const datum = (wert) => datumUndZeit(wert, { leer: '' });

export default function MailTemplates() {
  const [liste, setListe] = useState(null);
  const [listenFehler, setListenFehler] = useState(false);
  const [suche, setSuche] = useState('');
  const [key, setKey] = useState(null);
  const [vorlage, setVorlage] = useState(null);
  const [betreff, setBetreff] = useState('');
  const [text, setText] = useState('');
  const [fehler, setFehler] = useState([]);
  const [vorschau, setVorschau] = useState(null);
  const [beschaeftigt, setBeschaeftigt] = useState(false);
  const [fragtReset, setFragtReset] = useState(false);
  const textRef = useRef(null);
  const editorRef = useRef({ key: null, selection: 0, draft: 0, pending: 0, busy: false });
  const listRequestRef = useRef(0);

  const ladeListe = useCallback(() => {
    const request = ++listRequestRef.current;
    setListenFehler(false);
    return api.get('/admin/mail-templates')
      .then((r) => { if (request === listRequestRef.current) setListe(r.data.templates); })
      .catch(() => { if (request === listRequestRef.current) setListenFehler(true); });
  }, []);
  useEffect(() => {
    ladeListe();
    return () => { editorRef.current.selection++; listRequestRef.current++; };
  }, [ladeListe]);

  const aktuell = (request, draft = true) => {
    const editor = editorRef.current;
    return editor.key === request.key && editor.selection === request.selection
      && editor.pending === request.pending && (!draft || editor.draft === request.draft);
  };
  const anfrage = () => {
    const editor = editorRef.current;
    if (!vorlage || vorlage.key !== key || editor.key !== key || editor.busy) return null;
    editor.pending++;
    editor.busy = true;
    setBeschaeftigt(true);
    return { ...editor };
  };
  const beenden = (request) => {
    if (aktuell(request, false)) {
      editorRef.current.busy = false;
      setBeschaeftigt(false);
    }
  };
  const aendern = (update) => {
    editorRef.current.draft++;
    setFehler([]);
    setVorschau(null);
    update();
  };

  const uebernehmen = useCallback((t) => {
    if (t.key !== editorRef.current.key) return;
    editorRef.current.draft++;
    setVorlage(t);
    setBetreff(t.current.subject);
    setText(t.current.body);
    setFehler([]);
    setVorschau(null);
  }, []);

  const oeffnen = useCallback((k) => {
    const editor = editorRef.current;
    editor.key = k;
    editor.selection++;
    editor.draft++;
    editor.pending++;
    editor.busy = false;
    const request = { ...editor };
    setKey(k);
    setVorlage(null);
    setFehler([]);
    setVorschau(null);
    setFragtReset(false);
    setBeschaeftigt(false);
    api.get(`/admin/mail-templates/${k}`)
      .then((r) => {
        if (editorRef.current.selection === request.selection && editorRef.current.key === k) uebernehmen(r.data.template);
      })
      .catch((e) => {
        if (editorRef.current.selection === request.selection && editorRef.current.key === k) toast.error(fehlertext(e, 'Vorlage konnte nicht geladen werden'));
      });
  }, [uebernehmen]);

  const gruppen = useMemo(() => {
    const q = suche.trim().toLowerCase();
    const out = [];
    for (const t of liste || []) {
      if (q && !`${t.titel} ${t.empfaenger} ${t.gruppe}`.toLowerCase().includes(q)) continue;
      let g = out.find((x) => x.name === t.gruppe);
      if (!g) { g = { name: t.gruppe, items: [] }; out.push(g); }
      g.items.push(t);
    }
    return out;
  }, [liste, suche]);

  const geaendert = !!vorlage && (betreff !== vorlage.current.subject || text !== vorlage.current.body);
  const anzahlGeaendert = (liste || []).filter((t) => t.overridden).length;

  const einfuegen = (name) => {
    editorRef.current.draft++;
    setFehler([]);
    setVorschau(null);
    const el = textRef.current;
    const marke = `{{${name}}}`;
    if (!el || typeof el.selectionStart !== 'number') { setText((v) => v + marke); return; }
    const start = el.selectionStart;
    const ende = el.selectionEnd;
    setText((v) => v.slice(0, start) + marke + v.slice(ende));
    requestAnimationFrame(() => { el.focus(); el.setSelectionRange(start + marke.length, start + marke.length); });
  };

  const zeigeVorschau = async () => {
    const request = anfrage();
    if (!request) return;
    try {
      const { data } = await api.post(`/admin/mail-templates/${request.key}/preview`, { subject: betreff, body: text });
      if (!aktuell(request)) return;
      setFehler(data.ok ? [] : data.errors);
      setVorschau(data.ok ? data : null);
    } catch (e) { if (aktuell(request)) toast.error(fehlertext(e, 'Vorschau fehlgeschlagen')); }
    finally { beenden(request); }
  };

  const speichern = async () => {
    const request = anfrage();
    if (!request) return;
    try {
      const { data } = await api.put(`/admin/mail-templates/${request.key}`, { subject: betreff, body: text, expected_revision: vorlage.revision });
      if (data.template.key === request.key) ladeListe();
      if (!aktuell(request, false) || data.template.key !== request.key) return;
      if (!aktuell(request)) {
        setVorlage(data.template);
        return;
      }
      uebernehmen(data.template);
      toast.success('Gespeichert — gilt ab der nächsten Mail');
    } catch (e) {
      if (!aktuell(request)) return;
      const d = e?.response?.data;
      if (d?.errors) setFehler(d.errors);
      toast.error(fehlertext(e, 'Speichern fehlgeschlagen'));
    } finally { beenden(request); }
  };

  const zuruecksetzen = async () => {
    setFragtReset(false);
    const request = anfrage();
    if (!request) return;
    try {
      const { data } = await api.post(`/admin/mail-templates/${request.key}/reset`, { expected_revision: vorlage.revision });
      if (data.template.key === request.key) ladeListe();
      if (!aktuell(request, false) || data.template.key !== request.key) return;
      if (!aktuell(request)) {
        setVorlage(data.template);
        return;
      }
      uebernehmen(data.template);
      toast.success('Standardtext gilt wieder');
    } catch (e) { if (aktuell(request)) toast.error(fehlertext(e, 'Zurücksetzen fehlgeschlagen')); }
    finally { beenden(request); }
  };

  const testmail = async () => {
    const request = anfrage();
    if (!request) return;
    try {
      const { data } = await api.post(`/admin/mail-templates/${request.key}/test`, { subject: betreff, body: text });
      if (!aktuell(request)) return;
      toast.success(`Testmail an ${data.to} verschickt`);
    } catch (e) {
      if (!aktuell(request)) return;
      const d = e?.response?.data;
      if (d?.errors) setFehler(d.errors);
      toast.error(fehlertext(e, 'Testmail fehlgeschlagen'));
    } finally { beenden(request); }
  };

  return (
    <div className="mt-root">
      <PageHeader
        title="E-Mail-Vorlagen"
        description={`Texte aller E-Mails an Kunden, Fahrer und Partner. Eine Änderung gilt ab der nächsten Mail, ohne Auslieferung.${liste ? ` ${anzahlGeaendert} von ${liste.length} Vorlagen sind geändert.` : ''}`}
      />
      <div className="mt-layout">
        <nav className="mt-list" aria-label="Vorlagen">
          <input className="mt-search" type="search" placeholder="Vorlage suchen" aria-label="Vorlage suchen" value={suche} onChange={(e) => setSuche(e.target.value)} />
          {listenFehler && <ErrorBox text="Vorlagen konnten nicht geladen werden." onRetry={ladeListe} />}
          {!liste && !listenFehler && <Skeleton h={220} />}
          {liste && gruppen.length === 0 && <Empty title="Keine Vorlage gefunden" />}
          {gruppen.map((g) => (
            <div key={g.name}>
              <div className="mt-group">{g.name}</div>
              {g.items.map((t) => (
                <button key={t.key} type="button" className={`mt-item${t.key === key ? ' is-active' : ''}`} onClick={() => oeffnen(t.key)}>
                  <span>
                    {t.titel}
                    <span className="mt-item-sub">{t.empfaenger}</span>
                  </span>
                  {t.overridden && <span className="mt-dot">geändert</span>}
                </button>
              ))}
            </div>
          ))}
        </nav>

        <section className="mt-editor" aria-label="Vorlage bearbeiten">
          {!key && <Empty title="Vorlage auswählen" hint="Links eine Vorlage anklicken, um den Text zu lesen oder zu ändern." />}
          {key && !vorlage && <Skeleton h={320} />}
          {vorlage && (
            <>
              <div className="mt-head">
                <div>
                  <h2 className="mt-title">{vorlage.titel}</h2>
                  <p className="mt-meta">
                    Geht an: {vorlage.empfaenger} · {vorlage.overridden ? `geändert am ${datum(vorlage.updated_at)}` : 'Standardtext'}
                  </p>
                </div>
              </div>
              <div className="mt-anlass">{vorlage.anlass}</div>

              <label className="mt-label" htmlFor="mt-betreff">Betreff</label>
              <input id="mt-betreff" className="mt-input" value={betreff} maxLength={vorlage.limits.subject} onChange={(e) => aendern(() => setBetreff(e.target.value))} />

              <label className="mt-label" htmlFor="mt-text">Text</label>
              <textarea id="mt-text" ref={textRef} className="mt-textarea" value={text} onChange={(e) => aendern(() => setText(e.target.value))} />
              <div className="mt-count">{text.length} / {vorlage.limits.body} Zeichen</div>

              {vorlage.platzhalter.length > 0 && (
                <>
                  <div className="mt-label">Platzhalter (anklicken zum Einfügen)</div>
                  <div className="mt-chips">
                    {vorlage.platzhalter.map((p) => (
                      <button key={p.name} type="button" className={`mt-chip${p.pflicht ? ' is-required' : ''}`} title={`${p.info} — Beispiel: ${p.beispiel}${p.pflicht ? ' (Pflicht)' : ''}`} onClick={() => einfuegen(p.name)}>
                        {`{{${p.name}}}`}
                      </button>
                    ))}
                  </div>
                </>
              )}
              <div className="mt-help">
                <code>{'{{name}}'}</code> setzt den Wert ein · <code>{'{{name|Ersatz}}'}</code> nimmt den Ersatz, wenn der Wert leer ist ·{' '}
                <code>{'{{#name}}…{{/name}}'}</code> zeigt den Abschnitt nur, wenn der Wert gefüllt ist ·{' '}
                <code>{'{{anzahl?Wort|Wörter}}'}</code> schreibt die Zahl mit dem passenden Wort.
                {vorlage.pflicht.length > 0 && ' Hervorgehobene Platzhalter müssen im Text vorkommen.'}
                {vorlage.hat_html && ' Die gestaltete Fassung dieser Mail wird aus deinem Text automatisch erzeugt.'}
              </div>

              {fehler.length > 0 && (
                <div className="mt-errors" role="alert">
                  <ul>{fehler.map((f) => <li key={f}>{f}</li>)}</ul>
                </div>
              )}

              <div className="mt-actions">
                <Btn onClick={speichern} disabled={beschaeftigt || !geaendert}><Save size={15} /> Speichern</Btn>
                <Btn variant="secondary" onClick={zeigeVorschau} disabled={beschaeftigt}><Eye size={15} /> Vorschau</Btn>
                <Btn variant="secondary" onClick={testmail} disabled={beschaeftigt}><Send size={15} /> Testmail an mich</Btn>
                <span className="mt-actions-spacer" />
                {geaendert && <Btn variant="ghost" onClick={() => uebernehmen(vorlage)} disabled={beschaeftigt}>Änderung verwerfen</Btn>}
                {vorlage.overridden && <Btn variant="ghost" onClick={() => setFragtReset(true)} disabled={beschaeftigt}><RotateCcw size={15} /> Auf Standard zurücksetzen</Btn>}
              </div>

              {vorschau && (
                <div className="mt-preview">
                  <div className="mt-section-title">Vorschau mit Beispielwerten</div>
                  <div className="mt-preview-box">
                    <div className="mt-preview-subject">{vorschau.subject}</div>
                    <pre className="mt-preview-text">{vorschau.text}</pre>
                  </div>
                </div>
              )}

              <div className="mt-preview">
                <div className="mt-section-title">Standardtext</div>
                <div className="mt-preview-box">
                  <div className="mt-preview-subject">{vorlage.standard.subject}</div>
                  <pre className="mt-preview-text">{vorlage.standard.body}</pre>
                </div>
                {(betreff !== vorlage.standard.subject || text !== vorlage.standard.body) && (
                  <div className="mt-help">
                    <button type="button" className="mt-link" onClick={() => aendern(() => { setBetreff(vorlage.standard.subject); setText(vorlage.standard.body); })}>Standardtext in den Editor laden</button>
                  </div>
                )}
              </div>

              {vorlage.history.length > 0 && (
                <div className="mt-history">
                  <div className="mt-section-title">Verlauf</div>
                  {vorlage.history.map((h) => (
                    <div key={h.revision} className="mt-history-row">
                      <span>
                        Fassung {h.revision} · {h.action === 'save' ? 'geändert' : 'auf Standard zurückgesetzt'} · {datum(h.created_at)}{h.actor_name ? ` · ${h.actor_name}` : ''}
                      </span>
                      {h.action === 'save' && (
                        <button type="button" className="mt-link" onClick={() => aendern(() => { setBetreff(h.subject); setText(h.body); })}>In den Editor laden</button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </section>
      </div>
      <ConfirmModal
        open={fragtReset}
        title="Auf Standardtext zurücksetzen?"
        body="Ab der nächsten Mail gilt wieder der Standardtext. Deine bisherigen Fassungen bleiben im Verlauf erhalten."
        confirmLabel="Zurücksetzen"
        onConfirm={zuruecksetzen}
        onCancel={() => setFragtReset(false)}
      />
    </div>
  );
}
